import { useMemo, useState } from 'react';
import {
  DEFAULT_HARDWARE_CONFIG,
  SENSORS,
  TELESCOPE_PACKAGES,
} from '../data/hardwareCatalog';
import { computeHardwareCost } from '../lib/computeHardwareCost';

function formatUsd(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export default function HardwareCostingModal({
  open,
  onClose,
  initialConfig,
  totalModules,
  onApply,
}) {
  const [telescopePackageId, setTelescopePackageId] = useState(
    initialConfig?.telescopePackageId ?? DEFAULT_HARDWARE_CONFIG.telescopePackageId,
  );
  const [sensorId, setSensorId] = useState(
    initialConfig?.sensorId ?? DEFAULT_HARDWARE_CONFIG.sensorId,
  );
  const [infrastructure, setInfrastructure] = useState(
    initialConfig?.infrastructure ?? DEFAULT_HARDWARE_CONFIG.infrastructure,
  );
  const [scaleQhyCompute, setScaleQhyCompute] = useState(
    (initialConfig?.sensorId ?? DEFAULT_HARDWARE_CONFIG.sensorId) === 'qhy411',
  );

  const result = useMemo(
    () =>
      computeHardwareCost({
        telescopePackageId,
        sensorId,
        infrastructure,
      }),
    [telescopePackageId, sensorId, infrastructure],
  );

  if (!open) return null;

  const networkHardwareTotal = result.hardwareCostPerModule * totalModules;

  function handleInfrastructureChange(lineId, value) {
    setInfrastructure((prev) => ({
      ...prev,
      [lineId]: parseFloat(value) || 0,
    }));
  }

  function handleSensorChange(id) {
    setSensorId(id);
    if (id === 'qhy411') {
      setScaleQhyCompute(true);
    }
  }

  function handleApply() {
    const applyCompute =
      sensorId === 'qhy411' &&
      scaleQhyCompute &&
      result.computeCostPerModule != null;

    onApply({
      config: {
        telescopePackageId,
        sensorId,
        infrastructure: { ...infrastructure },
      },
      hardwareCostPerModule: result.hardwareCostPerModule,
      computeCostPerModule: applyCompute
        ? result.computeCostPerModule
        : undefined,
      telescopesPerModule: result.telescopesPerModule,
      telescopeSpecs: result.telescopeSpecs,
    });
    onClose();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative glass-modal rounded-3xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-amber-200/80 hover:text-amber-100 text-xl transition-colors"
        >
          ✕
        </button>

        <h2 className="text-lg lg:text-xl font-thin mb-2 tracking-wider text-amber-100">
          HARDWARE COSTING
        </h2>
        <p className="text-xs text-amber-300/60 mb-6">
          Configure telescope and sensor packages. Apply rolls up into Observatory
          Hardware (and optional Compute) in Cost Projection.
        </p>

        <div className="mb-6">
          <h3 className="text-xs font-orbitron text-amber-100 tracking-wider mb-3">
            TELESCOPE PACKAGE
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.values(TELESCOPE_PACKAGES).map((pkg) => (
              <button
                key={pkg.id}
                type="button"
                onClick={() => setTelescopePackageId(pkg.id)}
                className={`p-4 rounded-xl text-left transition-all ${
                  telescopePackageId === pkg.id
                    ? 'glass-button-active border border-amber-500/40 text-amber-100'
                    : 'glass-button text-amber-200/80 hover:text-amber-100'
                }`}
              >
                <div className="font-orbitron text-sm tracking-wider">{pkg.label}</div>
                <div className="text-xs text-amber-300/60 mt-1">
                  {formatUsd(pkg.unitCost)} / OTA · {pkg.count} per module
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-xs font-orbitron text-amber-100 tracking-wider mb-3">
            SENSOR
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {Object.values(SENSORS).map((sensor) => (
              <button
                key={sensor.id}
                type="button"
                onClick={() => handleSensorChange(sensor.id)}
                className={`p-4 rounded-xl text-left transition-all ${
                  sensorId === sensor.id
                    ? 'glass-button-active border border-amber-500/40 text-amber-100'
                    : 'glass-button text-amber-200/80 hover:text-amber-100'
                }`}
              >
                <div className="font-orbitron text-sm tracking-wider">{sensor.label}</div>
                <div className="text-xs text-amber-300/60 mt-1">
                  {formatUsd(sensor.unitCost)} / camera
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-xs font-orbitron text-amber-100 tracking-wider mb-3">
            BILL OF MATERIALS (PER MODULE)
          </h3>
          <div className="bg-orange-900/10 border border-orange-500/20 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-orange-500/30">
                  <th className="text-left px-4 py-2 font-orbitron text-orange-200 text-xs tracking-wider">
                    LINE ITEM
                  </th>
                  <th className="text-right px-4 py-2 font-orbitron text-orange-200 text-xs tracking-wider">
                    QTY
                  </th>
                  <th className="text-right px-4 py-2 font-orbitron text-orange-200 text-xs tracking-wider">
                    UNIT
                  </th>
                  <th className="text-right px-4 py-2 font-orbitron text-orange-200 text-xs tracking-wider">
                    SUBTOTAL
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.breakdown.map((row) => (
                  <tr key={row.id} className="border-b border-orange-500/10">
                    <td className="px-4 py-2 text-amber-100/90">{row.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-amber-200/80">
                      {row.qty}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {row.editable ? (
                        <input
                          type="number"
                          min="0"
                          step="1000"
                          value={row.unitCost}
                          onChange={(e) =>
                            handleInfrastructureChange(row.id, e.target.value)
                          }
                          className="w-28 glass-input rounded px-2 py-1 text-right font-mono text-amber-100 text-xs"
                        />
                      ) : (
                        <span className="font-mono text-amber-200/80">
                          {formatUsd(row.unitCost)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-amber-100">
                      {formatUsd(row.subtotal)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-orange-800/20">
                  <td
                    colSpan={3}
                    className="px-4 py-3 font-orbitron text-amber-100 tracking-wider text-xs"
                  >
                    HARDWARE COST PER MODULE
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-orange-200">
                    {formatUsd(result.hardwareCostPerModule)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-amber-300/50 mt-2">
            Network ({totalModules} modules): {formatUsd(networkHardwareTotal)} hardware
            (before profit margin)
          </p>
        </div>

        {sensorId === 'qhy411' && result.computeCostPerModule != null ? (
          <label className="flex flex-col gap-1 mb-6 cursor-pointer">
            <span className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={scaleQhyCompute}
                onChange={(e) => setScaleQhyCompute(e.target.checked)}
                className="w-5 h-5 glass-input border-2 border-glass-border-strong rounded"
              />
              <span className="text-sm text-amber-200/80">
                Scale compute cost for QHY megapixels
              </span>
            </span>
            {scaleQhyCompute ? (
              <span className="text-xs text-amber-300/50 ml-8">
                Compute → {formatUsd(result.computeCostPerModule)} / module (151 MP,{' '}
                {result.telescopesPerModule} cameras vs 6×62 MP baseline)
              </span>
            ) : null}
          </label>
        ) : null}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="glass-button px-5 py-2 rounded-xl text-sm font-orbitron tracking-wider text-amber-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-5 py-2 rounded-xl text-sm font-orbitron tracking-wider bg-amber-600/30 border border-amber-500/50 text-amber-100 hover:bg-amber-600/40 transition-all"
          >
            Apply to Cost Projection
          </button>
        </div>
      </div>
    </div>
  );
}
